<script setup lang="ts">
/**
 * The context rail's view of the chat's current workflow negotiation (MAR-437 follow-up).
 *
 * "I never want any data the agent is working with to be invisible to the user" — the direct
 * request this exists to satisfy. `webChat.activeWorkflowRequest` finds the same `request` object
 * `WorkflowCard.vue` renders (by scanning the thread for the newest `run_workflow` card, never a
 * separate mirror) — this is a second, always-visible consumer of it, not a copy. A party pick here
 * and a party pick on the card are the SAME write, landing on the SAME `request.fields` array,
 * because both go through `WorkflowEntityPartyFill`'s `inputValues` prop bound to a proxy over it.
 *
 * Renders nothing with no open/completed negotiation — the common case for a chat that has never
 * touched a workflow.
 */
import { FileSignature } from 'lucide-vue-next'
import { computePartyBlocks, humanizeRole } from '~/utils/workflowPartyBlocks'

const { t } = useI18n()
const webChat = useWebChatStore()
const request = computed(() => webChat.activeWorkflowRequest)

/** `request.fields` as a plain `{key: value}` record — the shape `computePartyBlocks` and the
 *  read-only summary below both want; `request.fields` itself stays the array `WorkflowCard.vue`
 *  and the party-fill controls read and write directly. */
const values = computed<Record<string, unknown>>(() =>
  Object.fromEntries((request.value?.fields ?? []).map(f => [f.key, f.value])))

const partyBlocks = computed(() => request.value
  ? computePartyBlocks(request.value.fields, values.value)
  : [])

/** Fields not part of any party block — shown as a plain read-only tally, not editable here. The
 *  interactive surface this rail adds is the party picker; everything else stays a chat-card edit. */
const otherFieldsProgress = computed(() => {
  if (!request.value) return { done: 0, total: 0 }
  const partyKeys = new Set(partyBlocks.value.flatMap(b => b.targets.map(tgt => tgt.inputKey)))
  const rest = request.value.fields.filter(f => !partyKeys.has(f.key))
  const done = rest.filter((f) => {
    const v = f.value
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
  })
  return { done: done.length, total: rest.length }
})

const isDrafting = computed(() => request.value?.status === 'pending')

/**
 * `request.fields`, wrapped so a pick made HERE also reaches the persisted document (MAR-437
 * follow-up) — `WorkflowEntityPartyFill` writes `props.inputValues[key] = value` directly, with no
 * hook of its own, so without this a rail-driven pick would update the on-screen summary but never
 * sync to the server, leaving the previewed document silently out of date until the next model turn.
 * Mirrors the identical proxy in `WorkflowCard.vue`'s `values` — same mechanism, same choke point,
 * on both surfaces that can originate a write.
 */
const syncedValues = computed(() => {
  const req = request.value
  if (!req) return {}
  return new Proxy(values.value, {
    set: (_target, key: string, value) => {
      webChat.syncWorkflowField(req, String(key), value)
      return true
    },
  })
})
</script>

<template>
  <section v-if="request" class="border-t border-border px-2.5 py-3.5">
    <div class="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
      <FileSignature class="h-3.5 w-3.5 shrink-0" />
      <span class="min-w-0 flex-1 truncate">{{ request.workflowName }}</span>
      <span class="shrink-0 text-[11px]">
        {{ isDrafting ? t('contextRail.workflowDraft.drafting') : t('contextRail.workflowDraft.completed') }}
      </span>
    </div>

    <!-- Interactive while the proposal is still open — the same picker the chat card uses, bound to
         the SAME fields array, so a pick here is visible on the card immediately and vice versa. -->
    <template v-if="isDrafting">
      <WorkflowEntityPartyFill
        v-for="block in partyBlocks"
        :key="block.role"
        :role="block.role"
        :targets="block.targets"
        :id-type="block.idType"
        :kind="block.kind"
        :input-values="syncedValues"
      />
    </template>
    <!-- Once the run that used these values finished, the picker would change nothing — this is a
         record of what produced the document, not an open form. -->
    <ul v-else-if="partyBlocks.length" class="space-y-1 text-xs">
      <li v-for="block in partyBlocks" :key="block.role" class="flex gap-2">
        <span class="shrink-0 font-medium text-foreground">{{ humanizeRole(block.role) }}</span>
        <span class="min-w-0 flex-1 truncate text-muted-foreground">
          {{ values[block.targets.find(tgt => tgt.field === 'name' || tgt.field.endsWith('.name'))?.inputKey ?? ''] ?? '—' }}
        </span>
      </li>
    </ul>

    <p v-if="otherFieldsProgress.total" class="mt-1.5 text-[11px] text-muted-foreground">
      {{ t('contextRail.workflowDraft.fieldsCompleted', otherFieldsProgress) }}
    </p>
  </section>
</template>
